import { Avatar } from "@/elements/components/ui/avatar";
import React, { useCallback, useRef, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import ResponsiveContent from "../shared/responsive-content";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import Loader from "../shared/loader";
import TutorBioForm from "./tutor-bio-form";
import ProfilePhotoUploader from "./profile-photo-uploader";
import TutorAvailabilityForm from "./tutor-availability-form";
import { AppBar } from "../ui/app-bar";
import goBack from "../utils/go-back";

function TutorProfileSettings() {
  const [activeSection, setActiveSection] = useState<"bio" | "availability">(
    "bio"
  );
  const { data: tutorProfile, isLoading } = useQuery(
    convexQuery(api.tutor.profileSettings.queries.getProfile, {})
  );
  const {
    data: profileCompletionStatus,
    isLoading: isProfileCompletionStatusLoading,
  } = useQuery(
    convexQuery(
      api.tutor.profileSettings.queries.getProfileCompletionStatus,
      {}
    )
  );
  const scrollViewRef = useRef<ScrollView>(null);

  const handleScrollToTop = useCallback(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  }, []);

  if (isLoading || !tutorProfile || isProfileCompletionStatusLoading) {
    return <Loader />;
  }

  const { name, imageUrl } = tutorProfile;
  const percentage = profileCompletionStatus?.percentage ?? 0;
  const profileCompletedAt = profileCompletionStatus?.profileCompletedAt;

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-2" />
            <View className="gap-9">
              <View className="gap-1">
                <Text className="text-[32px] text-gray-800 leading-[1.2] font-medium font-zilla-slab-medium">
                  Profile settings
                </Text>
                <Text className="text-xl font-geologica-regular font-normal leading-[1.5] text-gray-800">
                  Manage your profile from here.
                </Text>
              </View>
              {!profileCompletedAt && (
                <View className="bg-teachLoop-creamDark rounded-2xl py-6 px-4 gap-2">
                  <View className="flex-row justify-between">
                    <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                      Profile completion
                    </Text>
                    <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                      {percentage}% done
                    </Text>
                  </View>
                  <View className="bg-gray-100 rounded-[5px] h-1">
                    <View
                      className={"bg-teal-500 h-1 rounded-[5px]"}
                      style={{
                        width: `${percentage}%`,
                      }}
                    />
                  </View>
                  <Text className="text-sm text-black leading-[1.5] font-normal font-geologica-regular">
                  Completed profiles are more likely to receive tuition requests.
                  </Text>
                </View>
              )}
              <View className="gap-2 items-center justify-center">
                <Avatar size="lg">
                  <Avatar.Content
                    size="lg"
                    label={name}
                    labelSize="lg"
                    source={imageUrl ? { uri: imageUrl } : undefined}
                  />
                </Avatar>
                <Text className="text-xl text-gray-800 leading-[1.5] font-medium font-geologica-medium">
                  {name}
                </Text>
                <ProfilePhotoUploader />
              </View>
              <View className="flex-row">
                <Pressable
                  className="justify-center items-center flex-1"
                  onPress={() => setActiveSection("bio")}
                >
                  <Text
                    className={`text-xl leading-[1.5] font-medium font-geologica-medium py-[11px] ${activeSection === "bio" ? "text-teal-700" : "text-gray-500"}`}
                  >
                    Bio
                  </Text>
                  {activeSection === "bio" ? (
                    <View className="w-full bg-teal-700 h-1" />
                  ) : (
                    <View className="w-full bg-gray-200 h-1" />
                  )}
                </Pressable>
                <Pressable
                  className="justify-center items-center flex-1"
                  onPress={() => setActiveSection("availability")}
                >
                  <Text
                    className={`text-xl leading-[1.5] font-medium font-geologica-medium py-[11px] ${activeSection === "availability" ? "text-teal-700" : "text-gray-500"}`}
                  >
                    Availability
                  </Text>
                  {activeSection === "availability" ? (
                    <View className="w-full bg-teal-700 h-1" />
                  ) : (
                    <View className="w-full bg-gray-200 h-1" />
                  )}
                </Pressable>
              </View>
              {activeSection === "bio" && (
                <TutorBioForm
                  initialData={tutorProfile}
                  onScrollToTop={handleScrollToTop}
                />
              )}

              {activeSection === "availability" && (
                <TutorAvailabilityForm initialData={tutorProfile} />
              )}
            </View>
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default TutorProfileSettings;
